import {
  Box,
  Typography,
  Stack,
  useMediaQuery,
  TextField,
  Dialog,
  DialogContent,
  IconButton,
  Fab,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../../Components/Common/Header";
import SearchBox from "../../Components/Search/SearchBox";
import { useLocation, useSearchParams } from "react-router-dom";
import { DoneSharp } from "@mui/icons-material";
import Footer from "../../Components/Common/Footer";
import axios from "axios";
import config from "../../config/config";
import { motion } from "framer-motion";
import SearchCard from "../../Components/Search/SearchCard";
import { logUserAction, createSearchFeedback } from "../../utils/analytics";
import SearchCategoryCard from "../../Components/Search/SearchCategoryCard";

const Search = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchCalledRef = React.useRef(false);

  const urlQuery = searchParams.get("q") || location.state?.search || "";

  const [search, setSearch] = useState(urlQuery);
  const [searchResults, setSearchResults] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const [categorySelect, setCategorySelect] = useState("handpik");
  const [categoryCollection, setCategoryCollection] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allResearchSteps, setAllResearchSteps] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [intervalComplete, setIntervalComplete] = useState(false);
  const [noResultAvailable, setNoResultAvailable] = useState(false);
  const [ipInfo, setIpInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState(urlQuery);

  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const [dots, setDots] = useState("");
  const [feedback, setFeedback] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [researchExpanded, setResearchExpanded] = React.useState(true);
  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);

  useEffect(() => {
    if (loading || !intervalComplete) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + "." : ""));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [loading, intervalComplete]);

  const getPercentage = (salePercentage) => {
    return (
      Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(salePercentage * 100) + "%"
    );
  };

  const getSearch = async () => {
    if (search === "") return;
    setCategoryCollection([]);
    setSearchResults([]);
    setNoResultAvailable(false);

    try {
      setSearchLoading(true);
      localStorage.setItem("query", search);
      const searchResult = await axios.get(
        `${config.backendUrl}/inventory/search?q=${encodeURIComponent(search)}`,
      );

      if (searchResult.status === 200) {
        const categories = searchResult.data?.results || [];

        setSearchResults(categories);
        setNoResultAvailable(categories.length === 0);

        await logUserAction({
          action: "search",
          data: {
            keyword: search,
            location:
              ipInfo?.city === undefined || ipInfo?.country === undefined
                ? null
                : `${ipInfo?.city}, ${ipInfo?.country}`,
          },
          timestamp: new Date().toISOString(),
        });

        await localStorage.setItem("searchResult", JSON.stringify(categories));

        return categories;
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setSearchLoading(false);
    }
    return [];
  };

  const postSearchFeedback = async () => {
    await createSearchFeedback(feedback, reason)
      .then(() => {
        console.log("Feedback submitted");
      })
      .catch((error) => {
        console.error("Error submitting feedback:", error);
      });
  };

  const getCollectionsByCategory = async () => {
    const searchResult = await axios.get(
      `${config.backendUrl}/inventory/list_of_categories?q=${search}`,
    );
    setAllCategories(
      searchResult.data?.results.sort((a, b) =>
        a.name === "On Sale" ? -1 : b.name === "On Sale" ? 1 : 0,
      ),
    );
    await localStorage.setItem(
      "categoryCollection",
      JSON.stringify(searchResult.data?.results),
    );
    // const sortedCategories = categories.sort((a, b) => (a.name === "On Sale" ? -1 : b.name === "On Sale" ? 1 : 0));
    // console.log("response", searchResult.data?.collections);
  };

  const getResearchSteps = async () => {
    if (!search) return;

    setSearchParams({ q: search }, { replace: false });

    localStorage.removeItem("query");
    localStorage.removeItem("searchResult");
    localStorage.removeItem("categoryCollection");
    localStorage.removeItem("researchSteps");

    searchCalledRef.current = true;

    setAllResearchSteps([]);
    setSearchResults([]);
    setCategoryCollection([]);
    setAllCategories([]);
    setIntervalComplete(false);
    setCurrentMessageIndex(0);
    setNoResultAvailable(false);
    setSearchQuery(search);
    setResearchExpanded(true);

    try {
      setLoading(true);
      const researchStepsRes = await axios.get(
        `${config.backendUrl}/inventory/research_steps?q=${search}`,
      );

      if (researchStepsRes.status === 200) {
        const steps = researchStepsRes.data.results.research_steps;
        setAllResearchSteps(steps);
        localStorage.setItem("researchSteps", JSON.stringify(steps));

        let index = 0;
        const interval = setInterval(() => {
          setCurrentMessageIndex(index);
          index++;
          if (index >= steps.length) {
            clearInterval(interval);
            setIntervalComplete(true);
          }
        }, 1000);
      }

      // Call both API requests in parallel
      await Promise.all([getSearch(), getCollectionsByCategory()]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    if (searchCalledRef.current) {
      console.log("Search already in progress, skipping duplicate call");
      return;
    }

    const query = await localStorage.getItem("query");
    const searchResult = await localStorage.getItem("searchResult");
    const categoryCollection = await localStorage.getItem("categoryCollection");
    const researchSteps = await localStorage.getItem("researchSteps");

    if (searchResult && query === search && search.trim() !== "") {
      console.log("Found in localStorage");
      const parsedResults = JSON.parse(searchResult);
      setSearchResults(parsedResults);

      if (categoryCollection) {
        setAllCategories(JSON.parse(categoryCollection));
      }

      if (researchSteps) {
        const parsedSteps = JSON.parse(researchSteps);
        setAllResearchSteps(parsedSteps);
        setCurrentMessageIndex(parsedSteps.length - 1);
      }

      setIntervalComplete(true);
      if (parsedResults.length === 0) {
        setNoResultAvailable(true);
      }
    } else if (search.trim() !== "" && !searchCalledRef.current) {
      console.log("Couldn't find in localStorage or query mismatch");
      getResearchSteps();
    }
  };

  useEffect(() => {
    const urlQuery = searchParams.get("q") || "";
    if (urlQuery.trim() !== "" && urlQuery === search && !searchCalledRef.current) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get("q") || "";
    if (urlQuery !== search) {
      setSearch(urlQuery);
      setSearchQuery(urlQuery);
      if (urlQuery.trim() !== "") {
        setCategorySelect("handpik");
        setCategoryCollection([]);
        if (!searchCalledRef.current) {
          fetchData();
        }
      } else {
        setSearchResults([]);
        setAllCategories([]);
        setAllResearchSteps([]);
        setNoResultAvailable(false);
        setIntervalComplete(true);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (intervalComplete || noResultAvailable) {
      searchCalledRef.current = false;
    }
  }, [intervalComplete, noResultAvailable]);

  useEffect(() => {
    axios
      .get("https://ipapi.co/json?access_key=e8d395e0f2c259f5c4b281680485dbd8")
      .then(async (data) => {
        setIpInfo({
          city: data.data?.city,
          country: data.data?.country_name,
        });
      })
      .catch((err) => console.error("Error fetching IP:", err));
  }, []);

  // Auto-collapse research when results are displayed
  useEffect(() => {
    if (intervalComplete && searchResults?.length > 0) {
      setResearchExpanded(false);
      // Scroll down just enough to hide the search box
      setTimeout(() => {
        const searchBox = document.querySelector('[class*="MuiBox"]');
        if (searchBox) {
          window.scrollTo({
            top: 150,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  }, [intervalComplete, searchResults]);

  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const mediumScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "lg"),
  );

  return (
    <Box sx={{
      backgroundColor: "#FFFFFF",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />

      <SearchBox
        search={search}
        setSearch={setSearch}
        startSearch={getResearchSteps}
        loading={loading}
        searchLoading={searchLoading}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            width: "100%",
            mx: "auto",
            px: smallScreen ? 2 : 3,
            py: smallScreen ? 2 : 2.5,
            position: "relative",
          }}
        >
        <Box>
          {/* Compact Search Status Badge - Only show while loading */}
          {(loading || !intervalComplete) && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1,
                mb: 2,
                backgroundColor: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor: "#FF7B54",
                  animation: "searchPulse 1.5s ease-in-out infinite",
                  "@keyframes searchPulse": {
                    "0%, 100%": { opacity: 0.5 },
                    "50%": { opacity: 1 },
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Poppins-Medium",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#6B7280",
                }}
              >
                Searching{dots}
              </Typography>
            </Box>
          )}
          {/* Collapsible Research Steps Box */}
          {allResearchSteps && allResearchSteps.length > 0 && (
            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                p: smallScreen ? 2 : 2.5,
                mb: 2,
                maxWidth: "100%",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Box
                onClick={() => setResearchExpanded(!researchExpanded)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  mb: researchExpanded ? (smallScreen ? 1.5 : 2) : 0,
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins-SemiBold",
                    fontSize: smallScreen ? "15px" : "16px",
                    fontWeight: 600,
                    color: "#1a1a1a",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Research
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#666",
                    lineHeight: 1,
                    transform: researchExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                >
                  ▼
                </Typography>
              </Box>
              {researchExpanded && (
                <Stack spacing={smallScreen ? 1.25 : 1.5}>
                  {(allResearchSteps || [])
                    .slice(0, currentMessageIndex + 1)
                    .map((msg, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      sx={{ alignItems: "flex-start", gap: 1.5 }}
                    >
                      {index === currentMessageIndex ? (
                        <Box
                          sx={{
                            height: smallScreen ? "20px" : "22px",
                            width: smallScreen ? "20px" : "22px",
                            borderRadius: "50%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#FF7B54",
                            flexShrink: 0,
                            animation: "pulse 2s ease-in-out infinite",
                            "@keyframes pulse": {
                              "0%, 100%": { opacity: 0.8 },
                              "50%": { opacity: 1 },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: smallScreen ? "6px" : "7px",
                              height: smallScreen ? "6px" : "7px",
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            height: smallScreen ? "20px" : "22px",
                            width: smallScreen ? "20px" : "22px",
                            borderRadius: "50%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#10B981",
                            flexShrink: 0,
                          }}
                        >
                          <DoneSharp sx={{ fontSize: smallScreen ? "11px" : "12px", color: "#fff" }} />
                        </Box>
                      )}

                      <Typography
                        sx={{
                          fontFamily: "Poppins-Regular",
                          fontSize: smallScreen ? "14px" : "14px",
                          color: "#4B5563",
                          lineHeight: 1.6,
                          pt: 0.25,
                        }}
                      >
                        {msg}
                      </Typography>
                    </Stack>
                    ))}
                </Stack>
              )}
            </Box>
          )}
          {false && !submitted &&
            researchExpanded &&
            currentMessageIndex === allResearchSteps.length - 1 &&
            searchResults?.length > 0 &&
            !searchLoading && (
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: smallScreen ? "13px" : "14px",
                    fontFamily: "Poppins-SemiBold",
                    fontWeight: 600,
                    color: "#1a1a1a",
                  }}
                >
                  How were these results?
                </Typography>

                <Stack direction="row" spacing={smallScreen ? 1 : 1.5} sx={{ flexWrap: "wrap", gap: smallScreen ? 1 : 0 }}>
                  <Box
                    onClick={() => {
                      setFeedback("up");
                      setSubmitted(true);
                      postSearchFeedback("up", "");
                    }}
                    sx={{
                      cursor: "pointer",
                      px: smallScreen ? 2 : 2.5,
                      py: smallScreen ? 0.875 : 1,
                      borderRadius: "12px",
                      fontSize: smallScreen ? "12px" : "13px",
                      fontFamily: "Poppins-Medium",
                      fontWeight: 500,
                      background: feedback === "up"
                        ? "linear-gradient(135deg, #ABCC8C 0%, #95B87A 100%)"
                        : "#f5f5f5",
                      color: feedback === "up" ? "#fff" : "#666",
                      border: feedback === "up" ? "none" : "1px solid rgba(0,0,0,0.06)",
                      boxShadow: feedback === "up" ? "0 2px 8px rgba(171,204,140,0.3)" : "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: feedback === "up"
                          ? "0 4px 12px rgba(171,204,140,0.4)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    👍 Helpful
                  </Box>

                  <Box
                    onClick={() => setFeedback("down")}
                    sx={{
                      cursor: "pointer",
                      px: smallScreen ? 2 : 2.5,
                      py: smallScreen ? 0.875 : 1,
                      borderRadius: "12px",
                      fontSize: smallScreen ? "12px" : "13px",
                      fontFamily: "Poppins-Medium",
                      fontWeight: 500,
                      background: feedback === "down"
                        ? "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)"
                        : "#f5f5f5",
                      color: feedback === "down" ? "#fff" : "#666",
                      border: feedback === "down" ? "none" : "1px solid rgba(0,0,0,0.06)",
                      boxShadow: feedback === "down" ? "0 2px 8px rgba(255,183,156,0.3)" : "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: feedback === "down"
                          ? "0 4px 12px rgba(255,183,156,0.4)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    👎 Not helpful
                  </Box>
                </Stack>

                {feedback === "down" && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 0.5 }}
                  >
                    <TextField
                      size="small"
                      placeholder="Tell us what went wrong…"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      multiline
                      rows={smallScreen ? 2 : 3}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          fontFamily: "Poppins-Regular",
                          fontSize: smallScreen ? "13px" : "14px",
                          backgroundColor: "#f9f9f9",
                          "&:hover": {
                            backgroundColor: "#fff",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#fff",
                          },
                        },
                      }}
                    />

                    <Box
                      onClick={() => {
                        if (!reason.trim()) return;
                        setSubmitted(true);
                        postSearchFeedback();
                      }}
                      sx={{
                        alignSelf: "flex-end",
                        cursor: reason.trim() ? "pointer" : "not-allowed",
                        background: reason.trim()
                          ? "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)"
                          : "#e0e0e0",
                        color: "#fff",
                        px: smallScreen ? 2.5 : 3,
                        py: smallScreen ? 0.75 : 0.875,
                        borderRadius: "12px",
                        fontSize: smallScreen ? "12px" : "13px",
                        fontFamily: "Poppins-Medium",
                        fontWeight: 500,
                        boxShadow: reason.trim() ? "0 2px 8px rgba(255,183,156,0.3)" : "none",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: reason.trim() ? "translateY(-1px)" : "none",
                          boxShadow: reason.trim()
                            ? "0 4px 12px rgba(255,183,156,0.4)"
                            : "none",
                        },
                        "&:active": {
                          transform: reason.trim() ? "translateY(0)" : "none",
                        },
                      }}
                    >
                      Submit Feedback
                    </Box>
                  </Box>
                )}
              </Box>
            )}
        </Box>

        {intervalComplete && searchResults?.length !== 0 && (
          <>
            {/* Sticky Category Section */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 99,
                backgroundColor: "#fff",
                pt: 2,
                pb: 2,
                mt: 0.5,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: smallScreen ? 1.25 : 1.5,
                  overflowX: "auto",
                  overflowY: "visible",
                  pb: 1,
                  pt: 0.5,
                  px: smallScreen ? 2 : 3,
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none",
                }}
              >
                <Box
                  onClick={() => {
                    setCategorySelect("handpik");
                    setCategoryCollection([]);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: smallScreen ? "70px" : "75px",
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: smallScreen ? "60px" : "65px",
                      height: smallScreen ? "60px" : "65px",
                      borderRadius: "50%",
                      background: categorySelect === "handpik"
                        ? "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)"
                        : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: categorySelect === "handpik"
                        ? "0 4px 16px rgba(255,183,156,0.3)"
                        : "0 2px 8px rgba(0,0,0,0.08)",
                      border: categorySelect === "handpik"
                        ? "2px solid #FFB79C"
                        : "2px solid rgba(0,0,0,0.08)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      animation: "categoryPulse 2s ease-in-out infinite",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "-4px",
                        left: "-4px",
                        right: "-4px",
                        bottom: "-4px",
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: categorySelect === "handpik"
                          ? "#FFB79C"
                          : "#9AE0EB",
                        opacity: 0,
                        animation: "ripple 1.5s ease-out infinite",
                      },
                      "@keyframes categoryPulse": {
                        "0%, 100%": {
                          transform: "scale(1)",
                          boxShadow: categorySelect === "handpik"
                            ? "0 4px 16px rgba(255,183,156,0.3)"
                            : "0 2px 8px rgba(0,0,0,0.08)",
                        },
                        "50%": {
                          transform: "scale(1.05)",
                          boxShadow: categorySelect === "handpik"
                            ? "0 6px 20px rgba(255,183,156,0.5)"
                            : "0 4px 12px rgba(0,0,0,0.12)",
                        },
                      },
                      "@keyframes ripple": {
                        "0%": {
                          transform: "scale(1)",
                          opacity: 0.8,
                        },
                        "100%": {
                          transform: "scale(1.4)",
                          opacity: 0,
                        },
                      },
                    }}
                  >
                    <img
                      src={require("../../assets/images/login-handpik-logo_cropped.png")}
                      alt="Handpik.ai"
                      style={{
                        width: smallScreen ? "45px" : "50px",
                        height: smallScreen ? "45px" : "50px",
                        objectFit: "contain",
                        filter: categorySelect === "handpik" ? "brightness(0) invert(1)" : "none",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins-SemiBold",
                      fontSize: smallScreen ? "10px" : "11px",
                      fontWeight: 600,
                      color: categorySelect === "handpik" ? "#1a1a1a" : "#666",
                      textAlign: "center",
                      transition: "color 0.3s ease",
                    }}
                  >
                    Handpicked
                  </Typography>
                </Box>

                {intervalComplete && allCategories?.length > 0
                  ? allCategories.map(
                      (item, index) =>
                        item?.products.length > 0 && (
                          <Box
                            key={index}
                            onClick={async () => {
                              setCategorySelect(item?.name);
                              setCategoryCollection(item?.products);

                              await logUserAction({
                                action: "category_click",
                                data: {
                                  category: item?.name || null,
                                },
                                timestamp: new Date().toISOString(),
                              });
                            }}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.75,
                              minWidth: smallScreen ? "70px" : "75px",
                              cursor: "pointer",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                transform: "translateY(-3px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: smallScreen ? "60px" : "65px",
                                height: smallScreen ? "60px" : "65px",
                                borderRadius: "50%",
                                overflow: "hidden",
                                backgroundColor: item?.name === "On Sale" ? "#fff" : "transparent",
                                border: categorySelect === item?.name
                                  ? "2px solid #FFB79C"
                                  : "2px solid rgba(0,0,0,0.08)",
                                boxShadow: categorySelect === item?.name
                                  ? "0 4px 16px rgba(255,183,156,0.3)"
                                  : "0 2px 8px rgba(0,0,0,0.08)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                animation: "categoryPulse 2s ease-in-out infinite",
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  top: "-4px",
                                  left: "-4px",
                                  right: "-4px",
                                  bottom: "-4px",
                                  borderRadius: "50%",
                                  border: "2px solid",
                                  borderColor: categorySelect === item?.name
                                    ? "#FFB79C"
                                    : "#9AE0EB",
                                  opacity: 0,
                                  animation: "ripple 1.5s ease-out infinite",
                                  zIndex: -1,
                                },
                                "@keyframes categoryPulse": {
                                  "0%, 100%": {
                                    transform: "scale(1)",
                                    boxShadow: categorySelect === item?.name
                                      ? "0 4px 16px rgba(255,183,156,0.3)"
                                      : "0 2px 8px rgba(0,0,0,0.08)",
                                  },
                                  "50%": {
                                    transform: "scale(1.05)",
                                    boxShadow: categorySelect === item?.name
                                      ? "0 6px 20px rgba(255,183,156,0.5)"
                                      : "0 4px 12px rgba(0,0,0,0.12)",
                                  },
                                },
                                "@keyframes ripple": {
                                  "0%": {
                                    transform: "scale(1)",
                                    opacity: 0.8,
                                  },
                                  "100%": {
                                    transform: "scale(1.4)",
                                    opacity: 0,
                                  },
                                },
                              }}
                            >
                              <img
                                src={
                                  item?.name === "On Sale"
                                    ? require("../../assets/images/onsale.png")
                                    : item?.products[0]?.image_url || item?.products[0]?.images?.[0]
                                }
                                alt={item?.name}
                                style={{
                                  width: item?.name === "On Sale" ? "70%" : "100%",
                                  height: item?.name === "On Sale" ? "70%" : "100%",
                                  objectFit: "contain",
                                  position: item?.name === "On Sale" ? "absolute" : "static",
                                  top: item?.name === "On Sale" ? "50%" : "auto",
                                  left: item?.name === "On Sale" ? "50%" : "auto",
                                  transform: item?.name === "On Sale" ? "translate(-50%, -50%)" : "none",
                                }}
                              />

                              {/* Selected overlay gradient */}
                              {categorySelect === item?.name && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: "linear-gradient(135deg, rgba(255,183,156,0.25) 0%, rgba(255,167,133,0.25) 100%)",
                                    zIndex: 1,
                                  }}
                                />
                              )}
                            </Box>

                            <Typography
                              sx={{
                                fontFamily: "Poppins-SemiBold",
                                fontSize: smallScreen ? "10px" : "11px",
                                fontWeight: 600,
                                color: categorySelect === item?.name ? "#1a1a1a" : "#666",
                                textAlign: "center",
                                transition: "color 0.3s ease",
                                maxWidth: smallScreen ? "70px" : "75px",
                              }}
                            >
                              {item?.name}
                            </Typography>
                          </Box>
                      ),
                  )
                : ""}
              </Box>
            </Box>
            {/* End Sticky Category Section */}
          </>
        )}

        {/* Card */}
        {intervalComplete &&
          categoryCollection.length === 0 &&
          searchResults?.length > 0 && (
            <SearchCard searchResults={searchResults} />
          )}

        {noResultAvailable && !searchLoading && (
          <Box
            sx={{
              borderRadius: "20px",
              backgroundColor: "#fff",
              boxShadow: "-1px -2px 15px 0px #00000012",
              p: 2,
              m: 2,
              overflowX: "visible",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold",
                fontSize: "14.21px",
                fontWeight: "600",
                color: "#000",
              }}
            >
              Unfortunately, I couldn't find any relevant products meeting your
              requirements
            </Typography>
          </Box>
        )}

        {categoryCollection?.length > 0 && (
          <SearchCategoryCard categoryCollection={categoryCollection} />
        )}
        </Box>
      </Box>

      {/* Floating Feedback Button */}
      {intervalComplete && searchResults?.length > 0 && !submitted && (
        <Box
          onClick={() => setFeedbackModalOpen(true)}
          sx={{
            position: "fixed",
            bottom: smallScreen ? 20 : 32,
            right: smallScreen ? 16 : 24,
            zIndex: 1000,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            background: "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)",
            px: smallScreen ? 1.5 : 2,
            py: smallScreen ? 1 : 1.25,
            borderRadius: "24px",
            boxShadow: "0 4px 20px rgba(255,183,156,0.4)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #FFA785 0%, #FF9970 100%)",
              boxShadow: "0 6px 28px rgba(255,183,156,0.5)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          <Typography sx={{ fontSize: smallScreen ? "18px" : "20px" }}>💭</Typography>
          <Typography
            sx={{
              fontFamily: "Poppins-Medium",
              fontSize: smallScreen ? "13px" : "14px",
              fontWeight: 500,
              color: "#fff",
            }}
          >
            Feedback
          </Typography>
        </Box>
      )}

      {/* Feedback Modal */}
      <Dialog
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: smallScreen ? 2 : 3,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: smallScreen ? "18px" : "20px",
                  fontFamily: "Poppins-Bold",
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                How were these results?
              </Typography>
              <IconButton
                onClick={() => setFeedbackModalOpen(false)}
                sx={{
                  color: "#666",
                  "&:hover": { color: "#1a1a1a" },
                }}
              >
                ✕
              </IconButton>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Box
                onClick={() => {
                  setFeedback("up");
                  setSubmitted(true);
                  postSearchFeedback("up", "");
                  setTimeout(() => setFeedbackModalOpen(false), 1000);
                }}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontFamily: "Poppins-Medium",
                  fontWeight: 500,
                  background: feedback === "up"
                    ? "linear-gradient(135deg, #ABCC8C 0%, #95B87A 100%)"
                    : "#f5f5f5",
                  color: feedback === "up" ? "#fff" : "#666",
                  border: feedback === "up" ? "none" : "1px solid rgba(0,0,0,0.06)",
                  boxShadow: feedback === "up" ? "0 2px 8px rgba(171,204,140,0.3)" : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  textAlign: "center",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: feedback === "up"
                      ? "0 4px 12px rgba(171,204,140,0.4)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                👍 Helpful
              </Box>

              <Box
                onClick={() => setFeedback("down")}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontFamily: "Poppins-Medium",
                  fontWeight: 500,
                  background: feedback === "down"
                    ? "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)"
                    : "#f5f5f5",
                  color: feedback === "down" ? "#fff" : "#666",
                  border: feedback === "down" ? "none" : "1px solid rgba(0,0,0,0.06)",
                  boxShadow: feedback === "down" ? "0 2px 8px rgba(255,183,156,0.3)" : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  textAlign: "center",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: feedback === "down"
                      ? "0 4px 12px rgba(255,183,156,0.4)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                👎 Not helpful
              </Box>
            </Stack>

            {feedback === "down" && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  size="small"
                  placeholder="Tell us what went wrong…"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={3}
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontFamily: "Poppins-Regular",
                      fontSize: "14px",
                      backgroundColor: "#f9f9f9",
                      "&:hover": {
                        backgroundColor: "#fff",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                <Box
                  onClick={() => {
                    if (!reason.trim()) return;
                    setSubmitted(true);
                    postSearchFeedback();
                    setTimeout(() => setFeedbackModalOpen(false), 1000);
                  }}
                  sx={{
                    alignSelf: "flex-end",
                    cursor: reason.trim() ? "pointer" : "not-allowed",
                    background: reason.trim()
                      ? "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)"
                      : "#e0e0e0",
                    color: "#fff",
                    px: 3,
                    py: 1,
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontFamily: "Poppins-Medium",
                    fontWeight: 500,
                    boxShadow: reason.trim() ? "0 2px 8px rgba(255,183,156,0.3)" : "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: reason.trim() ? "translateY(-1px)" : "none",
                      boxShadow: reason.trim()
                        ? "0 4px 12px rgba(255,183,156,0.4)"
                        : "none",
                    },
                    "&:active": {
                      transform: reason.trim() ? "translateY(0)" : "none",
                    },
                  }}
                >
                  Submit Feedback
                </Box>
              </Box>
            )}

            {submitted && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #ABCC8C 0%, #95B87A 100%)",
                  color: "#fff",
                  textAlign: "center",
                  fontFamily: "Poppins-Medium",
                }}
              >
                ✓ Thank you for your feedback!
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default Search;
